import {
  Box,
  Breadcrumbs,
  Button,
  Paper,
  Typography,
  Link,
} from "@mui/material";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  GridRowModes,
  DataGrid,
  GridActionsCellItem,
  GridRowEditStopReasons,
} from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import toast, { Toaster } from "react-hot-toast";
import Swal from "sweetalert2";
import * as services from "./../../services/saleng";
import { jwtDecode } from "jwt-decode";

const Products = () => {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const [loading, setLoading] = useState(true);
  const [shopId, setShopId] = useState(null);

  const currencyFormatter = new Intl.NumberFormat("th-TH", {
    // style: 'currency',
    style: "decimal",
    currency: "THB",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const thbPrice = {
    type: "number",
    width: 130,
    valueFormatter: (value) => currencyFormatter.format(value),
    cellClassName: "font-tabular-nums",
    renderCell: (params) => {
      const value = params.value;
      const color = value > 0 ? "green" : value <= 0 ? "red" : "black";
      return (
        <span style={{ color: color }}>{currencyFormatter.format(value)}</span>
      );
    },
  };

  const columns = [
    { field: "id", headerName: "ลำดับ", flex: 0, width: 70 },
    {
      field: "image",
      headerName: "รูปภาพ",
      flex: 0,
      width: 100,
      renderCell: (params) => {
        if (params.value) {
          return (
            <img
              src={params.value}
              style={{ width: "50px", height: "50px" }}
              alt=""
            />
          );
        }
      },
    },
    { field: "title", headerName: "ชื่อสินค้า", flex: 2 },
    { field: "subCategory", headerName: "หมวดหมู่ย่อย", flex: 1.5 },
    { field: "category", headerName: "ประเภท", flex: 1.5 },
    // {
    //   field: "price",
    //   headerName: "ราคาสินค้า",
    //   type: "number",
    //   editable: true,
    //   flex: 1,
    //   minWidth: 100,
    //   ...thbPrice,
    // },
    {
      field: "price_tier_1",
      headerName: "ราคาสินค้าระดับ VIP",
      type: "number",
      editable: true,
      flex: 1.5,
      minWidth: 120,
      ...thbPrice,
    },
    {
      field: "price_tier_2",
      headerName: "ราคาสินค้าระดับสมาชิก",
      type: "number",
      editable: true,
      flex: 1.5,
      minWidth: 120,
      ...thbPrice,
    },
    {
      field: "price_tier_3",
      headerName: "ราคาสินค้าระดับทั่วไป",
      type: "number",
      editable: true,
      flex: 1.5,
      minWidth: 120,
      ...thbPrice,
    },
    {
      field: "actions",
      headerName: "",
      flex: 0,
      headerAlign: "center",
      align: "center",
      renderCell: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              key={`${id}-save`}
              icon={<SaveIcon />}
              label="Save"
              sx={{
                color: "primary.main",
              }}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              key={`${id}-cancel`}
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            key={`${id}-edit`}
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            key={`${id}-delete`}
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(id)}
            color="inherit"
          />,
        ];
      },
    },
  ];

  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id) => () => {
    const deletedRow = rows.find((row) => row.id === id);

    Swal.fire({
      customClass: {
        container: "my-swal",
      },
      title: "ยืนยันที่จะลบ?",
      text: "คุณจะไม่สามารถย้อนกลับสิ่งนี้ได้!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "ยืนยัน",
      cancelButtonText: "ยกเลิก",
    }).then((result) => {
      if (result.isConfirmed) {
        services
          .deleteProduct(deletedRow.productId, shopId)
          .then((response) => {
            toast.success("ลบสำเร็จ!");
            Swal.fire({
              customClass: {
                container: "my-swal",
              },
              title: "ลบแล้ว!",
              text: `${deletedRow.title} ถูกลบแล้ว`,
              icon: "success",
              confirmButtonText: "ตกลง",
            });
            console.log("Product deleted successfully:", response);
            setRows((prevRows) =>
              prevRows.filter((row) => row.productId !== deletedRow.productId)
            );
          })
          .catch((error) => {
            toast.error("ลบไม่สำเร็จ!");
            console.error("Error deleting product:", error);
          });
      }
    });
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const processRowUpdate = (newRow) => {
    console.log("Cancel the edit and revert to the original data");

    const payload = {
      price: newRow.price,
      price_tier_1: newRow.price_tier_1,
      price_tier_2: newRow.price_tier_2,
      price_tier_3: newRow.price_tier_3,

    };
    services
      .updateProduct(newRow.productId, payload, shopId)
      .then((response) => {
        toast.success("แก้ไขสำเร็จ!");
        console.log("Product updated successfully:", response);
        const updatedRow = { ...newRow, isNew: false };
        setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
        return updatedRow;
      })
      .catch((error) => {
        toast.error("แก้ไขไม่สำเร็จ!");
        console.error("Error creating product:", error);
      });

    const originalRow = revertData(newRow);
    return originalRow; // คืนค่าผลลัพธ์เดิม
  };

  const revertData = (newRow) => {
    const originalRow = rows.find((row) => row.id === newRow.id);

    if (originalRow) {
      setRows(rows.map((row) => (row.id === newRow.id ? originalRow : row)));
    }

    setRowModesModel({
      ...rowModesModel,
      [newRow.id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    return originalRow;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!shopId) return;

        const response = await services.getProducts(shopId);
        const products = response.data.data;
          console.log(products)
        const productsWithIndex = products.map((item, index) => ({
          ...item,
          id: index + 1,
          productId: item.id,
        }));

        setRows(productsWithIndex);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    const accessToken = localStorage.getItem("accessToken");
    if (accessToken) {
      try {
        const decoded = jwtDecode(accessToken);
        if (decoded.id) {
          setShopId(decoded.id); // อัพเดตค่า shopId ผ่าน state
        }
      } catch (error) {
        console.error("Error decoding token:", error);
      }
    }

    fetchData();
  }, [shopId]);

  return (
    <>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mt: 2, mb: 2 }}>
        <Link underline="hover" color="inherit" href="/">
          หน้าแรก
        </Link>
        <Typography color="text.primary">รายการสินค้า</Typography>
      </Breadcrumbs>

      <Toaster position="bottom-left" reverseOrder={false} />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography component="h2" variant="h5">
          รายการสินค้า
        </Typography>

        <Button
          color="success"
          variant="contained"
          onClick={() => navigate("/products/add")}
        >
          เพิ่มรายการสินค้า
        </Button>
      </Box>
      <Box sx={{ flex: 1, position: "relative" }}>
        <Box sx={{ position: "absolute", inset: 0 }}>
          <Paper>
            <DataGrid
              rows={rows}
              columns={columns}
              editMode="row"
              rowModesModel={rowModesModel}
              onRowModesModelChange={handleRowModesModelChange}
              onRowEditStop={handleRowEditStop}
              processRowUpdate={processRowUpdate}
              loading={loading}
              slotProps={{
                toolbar: { setRows, setRowModesModel },
                loadingOverlay: {
                  variant: "skeleton",
                  noRowsVariant: "skeleton",
                },
              }}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 25 },
                },
              }}
              pageSizeOptions={[25, 50, 100]}
            />
          </Paper>
        </Box>
      </Box>
    </>
  );
};

export default Products;
